import { Notifier, TResponse, TWithIsOk, TWithIsOkAndData } from '@ws/shared/types';
import { Fetcher, getErrorMessage } from '@ws/shared/utils';

import { SyncRepository } from './Sync.repository';
import {
  IApplyEventRequestParams,
  IGetDeltaRequestParams,
  IInitSyncRequestParams,
  TApplyEventResponse,
  TGetDeltaResponse,
  TInitSyncResponse,
} from './types';

export class SyncService {
  private _repository: SyncRepository;
  private _notifier: Notifier;

  constructor(fetcher: Fetcher, notifier: Notifier) {
    this._repository = new SyncRepository(fetcher);
    this._notifier = notifier;
  }

  public async initSync(
    projectData: IInitSyncRequestParams,
  ): Promise<TWithIsOk<Partial<TInitSyncResponse>>> {
    let response: TResponse<TInitSyncResponse>;

    // initSync — user-initiated (включение синка из SyncModal/борда). В ОТЛИЧИЕ от фоновых
    // getDelta/applyEvent здесь тост УМЕСТЕН: пользователь явно нажал «включить» и ждёт
    // фидбек о результате. Спама нет — вызов one-shot, не в поллинг-цикле.
    try {
      response = await this._repository.initSync({
        eventId: projectData.eventId,
        project: projectData.project,
      });
    } catch (err) {
      console.log('initSync error', err, getErrorMessage(err));
      this._notifier.error(getErrorMessage(err));
      return { isOk: false };
    }

    if (response.error) {
      this._notifier.error(response.error.toString());
      return { isOk: false };
    }

    console.log('initSync response', response);

    if (!response.data) {
      this._notifier.error('Error during sync init request');
      return { isOk: false };
    }

    if (!response.data.lastUpdateId) {
      this._notifier.error('Error during sync init request: missing lastUpdateId');
      return { isOk: false };
    }

    return { isOk: true, ...response.data };
  }

  public async applyEvent(
    params: IApplyEventRequestParams,
  ): Promise<TWithIsOkAndData<TApplyEventResponse>> {
    let response: TResponse<TApplyEventResponse>;

    // applyEvent — фоновая отправка очереди (EventTrain.run). НЕ тостит: транзиентные сбои
    // уходят в retry-backoff, статус пробрасывается, а user-facing состояние показывает
    // супервизор (health → PAUSE(transport); 400 → ASK_DELTA). Завершение F13 toast-спама.
    try {
      response = await this._repository.applyEvent(params);
    } catch (err) {
      console.error('applyEvent transport error', getErrorMessage(err));
      return { isOk: false };
    }

    if (response.error) {
      return { isOk: false, status: response?.status };
    }

    if (!response.data) {
      return { isOk: false, status: response?.status };
    }

    return { isOk: true, status: response?.status, data: response.data };
  }

  public async getDelta(
    params: IGetDeltaRequestParams,
  ): Promise<TWithIsOk<Partial<TGetDeltaResponse>> & { status?: number }> {
    let response: TResponse<TGetDeltaResponse>;

    // getDelta — ЧИСТО фоновый поллинг (watch/checkDelta). Он НЕ тостит на сбоях: статус
    // пробрасывается наверх, а транспорт-состояние показывает супервизор через health →
    // PAUSE(transport)/«Ожидание сервера» (I3, завершение F13 toast-спама). Иначе каждый
    // провал поллинга при кратком падении gateway порождал бы error-тост (см. §5(а)).
    try {
      response = await this._repository.getDelta(params);
    } catch (err) {
      console.error('getDelta transport error', getErrorMessage(err));
      return { isOk: false };
    }

    if (response.error) {
      return { isOk: false, status: response?.status };
    }

    if (!response.data) {
      return { isOk: false, status: response?.status };
    }

    // Спред payload'а ДО status: настоящий HTTP-статус не должен перезатираться
    // одноимённым полем payload'а (его потребляет health-классификация MR-3).
    return { isOk: true, ...response.data, status: response?.status };
  }
}
